import { Plus, Pencil, Share2 } from "lucide-react";
import { useLanguage } from "@/context/LanguageContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import ProjectCombobox from "@/components/ProjectCombobox";

/**
 * 编辑报价单 dialog — quote number, customer (with 新增/编辑客户 buttons and the
 * currency / 价格系数 / 海运费 summary card), project and per-user sharing.
 * Extracted from pages/Quotes.jsx (2026-06 refactor); purely presentational.
 */
export default function QuoteEditDialog({
  editing, setEditing, onSave,
  customers, selectedCustomer, shippingFee, projects, globalRate,
  onNewCustomer, onEditCustomer,
  shareUsers, shareWith, shareEditors, onToggleShareUser, onSetSharePermission,
}) {
  const { t } = useLanguage();

  return (
    <Dialog open={Boolean(editing)} onOpenChange={(o) => !o && setEditing(null)}>
      <DialogContent className="max-w-md" data-testid="edit-quote-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading text-base">{t("quote.editTitle")} — {editing?.quote_number}</DialogTitle>
          <DialogDescription className="sr-only">{t("quote.editTitle")}</DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div className="space-y-1.5">
            <Label>{t("quote.quoteNumber")}</Label>
            <Input
              value={editing?.quote_number || ""}
              onChange={(e) => setEditing((q) => ({ ...q, quote_number: e.target.value }))}
              data-testid="edit-quote-number-input"
              className="font-mono"
            />
          </div>
          <div className="space-y-1.5">
            <div className="flex items-center justify-between gap-2">
              <Label>{t("quote.customer")}</Label>
              <div className="flex items-center gap-1.5">
                <Button type="button" variant="outline" size="sm"
                  onClick={onNewCustomer}
                  data-testid="quote-customer-new-button"
                  className="h-7 gap-1 text-xs rounded-md">
                  <Plus size={12} /> {t("quote.customerNew")}
                </Button>
                <Button type="button" variant="outline" size="sm"
                  disabled={!selectedCustomer}
                  onClick={onEditCustomer}
                  data-testid="quote-customer-edit-button"
                  className="h-7 gap-1 text-xs rounded-md">
                  <Pencil size={12} /> {t("quote.customerEdit")}
                </Button>
              </div>
            </div>
            <Select
              value={editing?.customer_name || "__none__"}
              onValueChange={(v) => setEditing((q) => ({ ...q, customer_name: v === "__none__" ? "" : v }))}
            >
              <SelectTrigger data-testid="edit-quote-customer-select">
                <SelectValue placeholder={t("quote.customerPlaceholder")} />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="__none__" data-testid="edit-quote-customer-option-none">-</SelectItem>
                {customers.map((c) => (
                  <SelectItem key={c} value={c} data-testid={`edit-quote-customer-option-${c}`}>{c}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            {selectedCustomer && (
              <div className="grid grid-cols-4 gap-2 rounded-md border border-border bg-secondary/30 px-3 py-2"
                data-testid="quote-customer-summary-card">
                <div className="min-w-0">
                  <p className="text-[10px] uppercase tracking-wide text-muted-foreground">{t("quote.customerName")}</p>
                  <p className="text-xs font-semibold truncate" data-testid="quote-customer-summary-name">{selectedCustomer.name}</p>
                </div>
                <div className="min-w-0">
                  <p className="text-[10px] uppercase tracking-wide text-muted-foreground">{t("customers.currency")}</p>
                  <p className="text-xs font-mono font-semibold" data-testid="quote-customer-summary-currency">{selectedCustomer.currency || "USD"}</p>
                </div>
                <div className="min-w-0">
                  <p className="text-[10px] uppercase tracking-wide text-muted-foreground">{t("quote.exchangeRate")}</p>
                  <Input
                    type="number" step="0.0001" min="0"
                    value={editing?.exchange_rate || ""}
                    placeholder={globalRate ? String(globalRate) : "7.2"}
                    onChange={(e) => setEditing((q) => ({ ...q, exchange_rate: e.target.value === "" ? 0 : parseFloat(e.target.value) }))}
                    data-testid="edit-quote-exchange-rate-input"
                    className="h-6 px-1.5 text-xs font-mono font-semibold"
                  />
                </div>
                <div className="min-w-0">
                  <p className="text-[10px] uppercase tracking-wide text-muted-foreground">{t("quote.customerFreightLabel")}</p>
                  <p className="text-xs font-mono font-semibold" data-testid="quote-customer-summary-freight">
                    {shippingFee ? `$${shippingFee}` : t("quote.customerFreightUnset")}
                  </p>
                </div>
              </div>
            )}
            {!selectedCustomer && (
              <div className="space-y-1">
                <Label className="text-xs">{t("quote.exchangeRate")}</Label>
                <Input
                  type="number" step="0.0001" min="0"
                  value={editing?.exchange_rate || ""}
                  placeholder={globalRate ? String(globalRate) : "7.2"}
                  onChange={(e) => setEditing((q) => ({ ...q, exchange_rate: e.target.value === "" ? 0 : parseFloat(e.target.value) }))}
                  data-testid="edit-quote-exchange-rate-input"
                  className="font-mono h-8"
                />
              </div>
            )}
            <p className="text-[11px] text-muted-foreground">{t("quote.customerFromModuleHint")}</p>
            <p className="text-[11px] text-muted-foreground">
              {t("quote.exchangeRateHint").replace("{rate}", globalRate ?? "-")}
            </p>
          </div>
          <div className="space-y-1.5">
            <Label>{t("quote.project")}</Label>
            <ProjectCombobox
              value={editing?.project || ""}
              onChange={(v) => setEditing((q) => ({ ...q, project: v }))}
              options={projects}
              placeholder={t("quote.projectPlaceholder")}
              testId="edit-quote-project"
            />
            <p className="text-[11px] text-muted-foreground">{t("quote.projectHint")}</p>
          </div>
          {/* 共享给 — per-quote sharing; works with or without a project. */}
          <div className="space-y-1.5">
            <Label className="flex items-center gap-1.5">
              <Share2 size={12} className="text-brand" /> {t("quote.shareWithLabel")}
            </Label>
            {shareUsers.length === 0 ? (
              <p className="text-[11px] text-muted-foreground">{t("quote.shareNoUsers")}</p>
            ) : (
              <div className="border border-border rounded-md max-h-40 overflow-y-auto divide-y divide-border/60" data-testid="edit-share-user-list">
                {shareUsers.map((u) => {
                  const emailKey = u.email.toLowerCase();
                  const checked = shareWith.has(emailKey);
                  const perm = shareEditors.has(emailKey) ? "edit" : "read";
                  return (
                    <div key={u.email}
                      className="flex items-center gap-2 px-2.5 py-1.5 hover:bg-secondary/40 transition-colors"
                      data-testid={`edit-share-user-row-${u.email}`}>
                      <label className="flex items-center gap-2 flex-1 min-w-0 cursor-pointer">
                        <Checkbox
                          checked={checked}
                          onCheckedChange={() => onToggleShareUser(u.email)}
                          data-testid={`edit-share-user-checkbox-${u.email}`}
                        />
                        <div className="flex-1 min-w-0">
                          <p className="text-xs font-medium truncate">{u.name || u.email}</p>
                          <p className="text-[10px] text-muted-foreground truncate">{u.email} · {u.role}</p>
                        </div>
                      </label>
                      <Select value={perm}
                        onValueChange={(v) => onSetSharePermission(u.email, v)}
                        disabled={!checked}>
                        <SelectTrigger className="h-7 text-[11px] w-[110px]"
                          data-testid={`edit-share-user-permission-${u.email}`}>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="read">{t("quote.sharePermissionRead")}</SelectItem>
                          <SelectItem value="edit">{t("quote.sharePermissionEdit")}</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  );
                })}
              </div>
            )}
            <p className="text-[11px] text-muted-foreground">{t("quote.shareQuoteHint")}</p>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => setEditing(null)} className="rounded-md">{t("common.cancel")}</Button>
          <Button onClick={onSave} data-testid="save-quote-edit-button" className="rounded-md bg-brand hover:bg-brand-hover">
            {t("common.save")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
